/**
 * Theme Context - Provides design tokens to all components
 */

// ✅ Import React properly
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import designTokens from '../styles/design-tokens'

// Create theme context
const ThemeContext = createContext(null)

// Theme variants
const themes = {
  light: {
    ...designTokens,
    colors: {
      ...designTokens.colors,
      surface: '#FFFFFF',
      background: '#FAF8F5',
      sidebar: '#F8F6F3',
      text: '#0F172A',
      textMuted: '#64748B',
      border: '#E5E7EB',
    },
  },
  dark: {
    ...designTokens,
    colors: {
      ...designTokens.colors,
      surface: '#1A1A2E',
      background: '#0F0F1A',
      sidebar: '#16162A',
      text: '#F1F5F9',
      textMuted: '#94A3B8',
      border: '#2D2D44',
    },
  },
}

// Storage key for theme preference
const THEME_STORAGE_KEY = 'bizos-theme'

export function ThemeProvider({ children, defaultTheme = 'light' }) {
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (stored && themes[stored]) return stored
    } catch {}
    return defaultTheme
  })

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.style.colorScheme = theme
    
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {}
  }, [theme])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme,
    tokens: themes[theme] || themes.light,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  }), [theme])

  // ✅ Use React.createElement with proper React import
  if (!mounted) {
    return children
  }

  return React.createElement(
    ThemeContext.Provider,
    { value },
    children
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export default ThemeProvider